import { z } from 'zod';
import { contactPhoneSchema } from './common.schemas';
import type { CodeSent } from './login-code.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';

export const loginSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const totpVerifyEnrollmentSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app'),
});
export type TotpVerifyEnrollmentInput = z.infer<typeof totpVerifyEnrollmentSchema>;

/** The second factors an office account can turn on. Order is login preference: the first enabled one is offered first. */
export const TWO_FACTOR_METHODS = ['totp', 'email'] as const;
export type TwoFactorMethod = (typeof TWO_FACTOR_METHODS)[number];

/**
 * `POST /auth/two-factor/verify`. A 6-digit code is checked against `method` (the authenticator app or the
 * code just emailed); anything else is tried as a recovery code, whichever method was chosen.
 */
export const twoFactorLoginVerifySchema = z.object({
  code: z.string().min(1),
  method: z.enum(TWO_FACTOR_METHODS),
});
export type TwoFactorLoginVerifyInput = z.infer<typeof twoFactorLoginVerifySchema>;

/** `POST /auth/email-otp/enable/verify`: the code emailed to prove the address receives mail before email codes are turned on. */
export const emailOtpVerifyEnableSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from the email'),
});
export type EmailOtpVerifyEnableInput = z.infer<typeof emailOtpVerifyEnableSchema>;

/** Response of the two routes that email a two-factor code (`POST /auth/email-otp/enable`, `POST /auth/two-factor/email-code`). */
export type EmailCodeSent = CodeSent;

/** Office passwords: long rather than complex. Checked when a password is set, never at login (old ones stay valid). */
export const PASSWORD_MIN_LENGTH = 12;

/** A password being set. Optional everywhere: without one, a person signs in with a code. */
export const newPasswordSchema = z.string().min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`).max(256);

export const provisionUserSchema = z.object({
  email: z.email().trim(),
  name: z.string().trim().min(1).max(120),
  /** Omitted: the account signs in with a code. Given for an existing user, it replaces their password. */
  password: newPasswordSchema.optional(),
  /** Role keys to assign, replacing whatever the user currently holds. Omitted leaves roles untouched. */
  roles: z.array(z.string()).optional(),
});
export type ProvisionUserInput = z.infer<typeof provisionUserSchema>;

/** `POST /users` — the admin "Create user" screen. Unlike `provisionUserSchema`, a duplicate email is always rejected, never a silent reactivate. */
export const createUserSchema = z.object({
  email: z.email().trim(),
  name: z.string().trim().min(1).max(120),
  phone: contactPhoneSchema.optional(),
  /** Omitted: the user signs in with a code sent to their email or phone, and can set a password themselves later. */
  password: newPasswordSchema.optional(),
  roles: z.array(z.string()).optional(),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

/** `PATCH /users/:id/roles` — sets the user's roles to exactly this list; an empty array removes all roles. */
export const updateUserRolesSchema = z.object({
  roles: z.array(z.string()),
});
export type UpdateUserRolesInput = z.infer<typeof updateUserRolesSchema>;

export const USER_STATUSES = ['active', 'inactive'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** `GET /users` — search matches name, email or phone; `role` keeps users holding any of the given roles. */
export const userListQuerySchema = defineListQuery({
  sortFields: ['name', 'email', 'lastLoginAt'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    status: listFilter(z.enum(USER_STATUSES)),
    role: listFilter(z.string().min(1).max(64)),
  },
});
export type UserListQuery = z.infer<typeof userListQuerySchema>;

/** `POST /auth/impersonation` — an admin starts acting as another user, for testing. `reason` lands on the audit entry. */
export const startImpersonationSchema = z.object({
  userId: z.uuid(),
  reason: z.string().trim().min(1).max(500).optional(),
});
export type StartImpersonationInput = z.infer<typeof startImpersonationSchema>;
